import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { useChatComposerFocus } from '@/components/molecule/ChatInput/useChatComposerFocus';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { EFaqsCategoryIcons } from '@/store/faq/config';
import { addQuestions, selectAllFAQs } from '@/store/faq/faqSlice';
import { TFAQsItem } from '@/store/faq/types';
import { getMappedFaqs } from '@/store/faq/utils';
import { selectActiveThreadId, selectIsThreadLoading } from '@/store/thread/threadSlice';

import {
  EQuestionPanelEntryMode,
  EQuestionPanelFocusTarget,
  TQuestionPanelState,
  TUseQuestionPanelOptions,
} from './types';

/**
 * State machine behind the two Question Panel entry points.
 *
 * Unstarted Thread arrival                → panel open unfiltered, both entry points withdrawn
 * close, no message sent yet              → Shortcut Bar revealed, Panel Toggle hidden
 * Category Shortcut activated             → panel open filtered to that category, Shortcut Bar withdrawn
 * close, message already sent             → Panel Toggle shown
 * any message sent (typed or selected)    → panel closes, Shortcut Bar withdrawn for the whole thread
 * Panel Toggle activated                  → panel opens unfiltered, toggling on repeat activation
 *
 * @param options - Composer context the transitions depend on plus the send handler.
 * @returns The panel state together with the transitions the entry components trigger.
 */
const useQuestionPanel = ({
  shouldShowEmptyState = false,
  isOnboarding = false,
  isLoading = false,
  handleSend,
}: TUseQuestionPanelOptions): TQuestionPanelState => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const { focusMessageInput, hasDraft } = useChatComposerFocus();

  const allFAQs = useAppSelector(selectAllFAQs);
  const activeThreadId = useAppSelector(selectActiveThreadId);
  const isThreadLoading = useAppSelector(selectIsThreadLoading(activeThreadId));

  // INFO: intl is stable for the page lifetime since a locale change remounts the tree.
  const mappedFAQs = useMemo(() => getMappedFaqs(intl), []);

  const [activeCategory, setActiveCategory] = useState<EFaqsCategoryIcons | null>(null);
  const [isPanelVisible, setIsPanelVisible] = useState(true);
  const [hasMessageBeenSent, setHasMessageBeenSent] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [originCategory, setOriginCategory] = useState<EFaqsCategoryIcons | null>(null);
  const [pendingFocus, setPendingFocus] = useState<EQuestionPanelFocusTarget | null>(null);

  // INFO: A draft fades the composer slot out, so the panel must become inert rather than sit
  // invisible but clickable. isPanelVisible is the user's intent and survives the draft, which is
  // what restores the panel in the same category once the input is cleared again.
  const isPanelOpen = isPanelVisible && !hasDraft;

  const entryMode =
    shouldShowEmptyState && !hasMessageBeenSent
      ? EQuestionPanelEntryMode.SHORTCUT_BAR
      : EQuestionPanelEntryMode.PANEL_TOGGLE;

  // INFO: Keyed off the intent rather than isPanelOpen, so a draft withdraws the panel without bringing the Shortcut Bar back underneath it.
  const isShortcutBarVisible = entryMode === EQuestionPanelEntryMode.SHORTCUT_BAR && !isPanelVisible;
  const isPanelToggleVisible = entryMode === EQuestionPanelEntryMode.PANEL_TOGGLE;

  const faqsList = useMemo(
    () =>
      (allFAQs ?? []).filter(
        (item) =>
          item.name.toLowerCase().includes(searchValue.toLowerCase()) &&
          (activeCategory ? item.icon === activeCategory : true),
      ),
    // INFO: allFAQs is the source, searchValue is the Search Term, activeCategory is the current Category Filter.
    // Therefore the search is performed within the activeCategory.
    [allFAQs, searchValue, activeCategory],
  );

  const selectCategory = useCallback((category: EFaqsCategoryIcons) => {
    setActiveCategory(category);
    setIsPanelVisible(true);
    setOriginCategory(category);
    setPendingFocus(EQuestionPanelFocusTarget.PANEL);
  }, []);

  const openPanel = useCallback(() => {
    setActiveCategory(null);
    setOriginCategory(null);
    setIsPanelVisible(true);
    setPendingFocus(EQuestionPanelFocusTarget.PANEL);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelVisible(false);
    setActiveCategory(null);
    setPendingFocus(
      entryMode === EQuestionPanelEntryMode.SHORTCUT_BAR
        ? EQuestionPanelFocusTarget.SHORTCUT
        : EQuestionPanelFocusTarget.TOGGLE,
    );
  }, [entryMode]);

  const consumePendingFocus = useCallback(() => setPendingFocus(null), []);

  const setFilterFromPanel = useCallback((category: EFaqsCategoryIcons | null) => {
    // INFO: Clicking the active Category filter clears it and shows all questions again. The panel
    // stays open either way — the close control is the only way out.
    setActiveCategory((current) => (category !== null && current === category ? null : category));
  }, []);

  const selectQuestion = useCallback(
    async (item: TFAQsItem) => {
      if (isLoading || isThreadLoading || !handleSend) return;

      setHasMessageBeenSent(true);
      setIsPanelVisible(false);
      setActiveCategory(null);
      // INFO: Focus goes to the Message Input directly, so no entry control may claim it afterwards.
      setPendingFocus(null);

      await handleSend({ content: item.name, isOnboarding });

      focusMessageInput();
    },
    [isLoading, isThreadLoading, handleSend, isOnboarding, focusMessageInput],
  );

  useEffect(() => {
    // INFO: mappedFAQs is the derived source data the store must mirror; dispatch is stable.
    dispatch(addQuestions(mappedFAQs));
  }, [mappedFAQs, dispatch]);

  useEffect(() => {
    // INFO: activeThreadId is the only trigger, switching threads invalidates both the open panel
    // and its Category Filter. An incoming Unstarted Thread reopens the panel through the effect
    // below, which is where the arrival state for such a thread is decided.
    setIsPanelVisible(false);
    setActiveCategory(null);
  }, [activeThreadId]);

  useEffect(() => {
    // INFO: shouldShowEmptyState turning true means a fresh Unstarted Thread. The panel is at the current
    // thread's arrival state, so the questions panel is visible before anything is typed, and the
    // "message already sent" latch is released so the Shortcut Bar (not the Panel Toggle) is the
    // entry point waiting underneath it. No focus is requested: nobody activated a control and the
    // composer's arrival focus owns the caret, so the panel is on screen to be read, not navigated.
    if (shouldShowEmptyState) {
      setHasMessageBeenSent(false);
      setIsPanelVisible(true);
    }
  }, [shouldShowEmptyState]);

  useEffect(() => {
    // INFO: isOnboarding is the only trigger for the Perform funnel. No focus is requested: the
    // composer's arrival focus owns focus at that point and must not be fought over.
    if (!isOnboarding) return;

    setActiveCategory(EFaqsCategoryIcons.PERFORM);
    setIsPanelVisible(true);
  }, [isOnboarding]);

  useEffect(() => {
    // INFO: isPanelOpen going false must clear the Search Term, so a reopened panel never shows a
    // Question List still narrowed by a term the user can no longer see.
    if (!isPanelOpen) setSearchValue('');
  }, [isPanelOpen]);

  useEffect(() => {
    // INFO: isLoading turning true means a message is in flight, typed or selected, and the panel must
    // not stay open over it. isPanelVisible is read in the condition so it is listed as a dependency:
    // re-running when the panel opens mid-flight closes it again, which is the same intent.
    if (!isLoading || !isPanelVisible) return;

    setHasMessageBeenSent(true);
    setIsPanelVisible(false);
    setActiveCategory(null);
  }, [isLoading, isPanelVisible]);

  useEffect(() => {
    // INFO: Escape is bound at window level, not on the panel, so it stays closable after focus has
    // left the panel's subtree. isPanelOpen gates the listener, closePanel is the action.
    if (!isPanelOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      // INFO: defaultPrevented lets nested widgets (the Search Control's field, dialogs) claim Escape keydown event first.
      if (event.key === 'Escape' && !event.defaultPrevented) closePanel();
    };

    window.addEventListener('keydown', handleEscape);

    return () => window.removeEventListener('keydown', handleEscape);
  }, [isPanelOpen, closePanel]);

  return useMemo(
    () => ({
      activeCategory,
      hasDraft,
      isPanelOpen,
      isShortcutBarVisible,
      isPanelToggleVisible,
      originCategory,
      pendingFocus,
      searchValue,
      faqsList,
      selectCategory,
      setFilterFromPanel,
      setSearchValue,
      closePanel,
      openPanel,
      selectQuestion,
      consumePendingFocus,
    }),
    [
      activeCategory,
      hasDraft,
      isPanelOpen,
      isShortcutBarVisible,
      isPanelToggleVisible,
      originCategory,
      pendingFocus,
      searchValue,
      faqsList,
      selectCategory,
      setFilterFromPanel,
      closePanel,
      openPanel,
      selectQuestion,
      consumePendingFocus,
    ],
  );
};

export default useQuestionPanel;
